import {AfterViewInit, Component, EventEmitter, Input, Output} from "@angular/core";
import {FUISpinnersService} from "@fedelta-pos/ui";
import {AppPaymentAccountService} from "../app-payment-account.service";
import {AppPaymentAccountData} from "../app-payment-account.data";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AppPaymentAccountCreateRequestData} from "../app-payment-account-create-request.data";
import {BehaviorSubject} from "rxjs";

@Component({
    selector: "fhm-app-payment-account-page-content-content",
    styleUrls: ["./app-payment-account-page-content.component.scss"],
    templateUrl: "./app-payment-account-page-content.component.html",
    providers: [AppPaymentAccountService],
    standalone: false
})
export class AppPaymentAccountPageContentComponent implements AfterViewInit {

    @Input() url: string;

    @Input() showClose: boolean;

    @Output() closed: EventEmitter<any> = new EventEmitter();

    appPaymentAccountData: AppPaymentAccountData;

    failed: boolean;
    expired: boolean;

    newBankAccountHolderName: string;
    newBankAccountBSB: string;
    newBankAccountNumber: string;
    newBankAccountHolderType: string;
    bankAccountForm: FormGroup;

    holderTypeOptions = [{value: "individual", label: "Individual"}, {value: "company", label: "Company"}];

    constructor(private spinnersService: FUISpinnersService,
                private service: AppPaymentAccountService,
                private formBuilder: FormBuilder) {
        this.bankAccountForm = this.formBuilder.group({
            newBankAccountHolderType: ["", Validators.required],
            newBankAccountHolderName: ["", Validators.required],
            newBankAccountBSB: ["", [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
            newBankAccountNumber: ["", [Validators.required, Validators.minLength(5), Validators.maxLength(9)]],
        });

        this.bankAccountForm.get("newBankAccountHolderType")["required$"] = new BehaviorSubject(true);
        this.bankAccountForm.get("newBankAccountHolderName")["required$"] = new BehaviorSubject(true);
        this.bankAccountForm.get("newBankAccountBSB")["required$"] = new BehaviorSubject(true);
        this.bankAccountForm.get("newBankAccountNumber")["required$"] = new BehaviorSubject(true);
    }

    ngAfterViewInit(): void {
        this.load();
    }

    load(): void {
        setTimeout(() => this.init(this.url ? this.url : window.location.hash));
    }

    async init(url: string): Promise<void> {
        this.expired = false;
        this.failed = false;
        const token: string = url.substr(url.indexOf("?d=") + 3);
        this.spinnersService.open();
        try {
            this.appPaymentAccountData = await this.service.getAppPaymentAccount(token);
        } catch (e) {
            if (e.status === 401) {
                this.expired = true;
            } else {
                this.failed = true;
            }
        } finally {
            this.spinnersService.close();
        }
    }

    get newAccount(): boolean {
        return this.appPaymentAccountData && !this.appPaymentAccountData.platformAccountId;
    }

    changeHolderType(type: any): void {
        this.newBankAccountHolderType = type.value;
    }

    async continue(): Promise<void> {
        Object.keys((this.bankAccountForm.controls)).forEach((key) => {
            this.bankAccountForm.controls[key].markAsTouched();
            this.bankAccountForm.controls[key].updateValueAndValidity();
        });

        if (this.bankAccountForm.valid) {
            this.spinnersService.open();
            try {
                const bankToken: string = await this.createBankAccountToken();
                this.appPaymentAccountData = await this.service.createAppPaymentAccount(new AppPaymentAccountCreateRequestData(bankToken));
                this.openVerifyAccount();
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async createBankAccountToken(): Promise<string> {
        const Stripe = window["Stripe"];

        const stripe: any = Stripe(this.appPaymentAccountData.stripePublicKey);
        const result = await stripe
            .createToken("bank_account", {
                country: "AU",
                currency: "AUD",
                routing_number: this.newBankAccountBSB,
                account_number: this.newBankAccountNumber,
                account_holder_name: this.newBankAccountHolderName,
                account_holder_type: this.newBankAccountHolderType
            });
        if (result.error) {
            throw new Error(result.error.message);
        }
        return result.token.id;
    }

    openVerifyAccount(): void {
        if (this.url) {
            window.open(this.appPaymentAccountData.verifyUrl, "_blank");
        } else {
            window.location.href = this.appPaymentAccountData.verifyUrl;
        }
    }
}
